import { Alert, AlertTitle, Button, Grid, Typography } from '@mui/material';
import * as yup from 'yup';
import { Box } from '@mui/system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllInvoices, sendSendingsByMissionId } from 'src/shared/services/invoiceService';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { useGlobals } from 'src/shared/contexts/appContext';
import { ArticlesLetterDescriptionEnum, getArticlesLetterItem } from 'src/shared/models/enums/articlesLetter.enum';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { InvoiceDeliveryMethodIntegerEnum } from 'src/shared/models/enums/invoiceDeliveryMethodInteger.enum';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { InvoiceSearchFilter } from '../models/invoiceSearchFilter';
import { SetSendingDate } from './setSendingDate';
import { SendSendingsByMissionIdRequest } from '../models/sendSendingsByMissionIdRequest';

type SendMemberInvoiceLetterProps = {
  modalOpen: boolean;
  onSave: () => void;
  onClose: () => void;
  invoiceMission: InvoiceMissionResponse;
  hideBackdrop?: boolean;
  sendingType: InvoiceSendingTypeIntegerEnum;
  navigateToInvoiceMission?: boolean;
};

type FormValues = {
  missionId: number;
  sndDate: string;
  deliveryMethod: InvoiceDeliveryMethodIntegerEnum;
  runAsTaskWithTaskService: boolean;
};

export const SendMemberInvoiceLetter = (props: SendMemberInvoiceLetterProps) => {
  const { modalOpen, onSave, onClose, invoiceMission, hideBackdrop = false, sendingType, navigateToInvoiceMission } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { setDateStamp, currentUser } = useGlobals();

  const isSuperAdmin = getPermission(currentUser, PermissionEnum.InvoiceCreateForCustomer);

  const [loading, setLoading] = useState<boolean>(false);
  const [totalUnsentInvoices, setTotalUnsentInvoices] = useState<number>(0);

  const InvoiceMailSenderSchema: yup.Schema<FormValues> = yup.object().shape({
    sndDate: yup.string().required(),
    missionId: yup.number().required(),
    deliveryMethod: yup.number().required(),
    runAsTaskWithTaskService: yup.boolean().required(),
  });

  const formMethods = useForm<FormValues>({
    resolver: yupResolver(InvoiceMailSenderSchema),
    mode: 'all',
    defaultValues: {
      sndDate: undefined,
      missionId: invoiceMission.invoiceMission_id,
      deliveryMethod: InvoiceDeliveryMethodIntegerEnum.letter,
      // Ska alltid köras via TaskService; superadmin kan kryssa ur manuellt, övriga får det automatiskt.
      runAsTaskWithTaskService: true,
    },
  });

  const { handleSubmit, watch } = formMethods;

  const searchFilter: InvoiceSearchFilter = useMemo(() => {
    return {
      ownerId: undefined,
      invoiceMissionId: invoiceMission?.invoiceMission_id,
      searchString: '',
      status: [],
      isSent: false,
      sendingType,
    } as unknown as InvoiceSearchFilter;
  }, [invoiceMission?.invoiceMission_id, sendingType]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getAllInvoices(0, 25, 'duedate', 'desc', searchFilter);
      if (isMounted()) {
        setTotalUnsentInvoices(result.data.totalItems);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, searchFilter]);
  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    const dataToPost: SendSendingsByMissionIdRequest = {
      missionId: data.missionId,
      sndDate: DateHelper.getDate(data.sndDate),
      deliveryMethod: data.deliveryMethod,
      async: true,
      sendingType,
      runAsTaskWithTaskService: data.runAsTaskWithTaskService,
    };
    ConsoleHelper.log('dataToPost', dataToPost);
    try {
      await sendSendingsByMissionId(dataToPost);
      if (isMounted()) {
        setDateStamp(new Date());
        toast.success(<>{t('invoice:articlesLetter.orderSent')}</>);
        onClose();
        if (navigateToInvoiceMission) {
          navigate(`${RoutesEnum.InvoiceMissionDetailSendingType}/${data.missionId}/${InvoiceSendingTypeIntegerEnum.NotSet}`);
        }
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
        toast.error(<>{t('invoice:articlesLetter.orderError')}</>);
      }
    } finally {
      setLoading(false);
    }
  };

  const getArticleData = () => {
    let articleItem;

    if (totalUnsentInvoices < 500) {
      articleItem = getArticlesLetterItem(ArticlesLetterDescriptionEnum.MA0117, t);
    } else if (totalUnsentInvoices >= 500 && totalUnsentInvoices < 5000) {
      articleItem = getArticlesLetterItem(ArticlesLetterDescriptionEnum.MA0118, t);
    } else if (totalUnsentInvoices >= 5000) {
      articleItem = getArticlesLetterItem(ArticlesLetterDescriptionEnum.MA0119, t);
    } else {
      articleItem = {
        price: 0,
        description: t('invoice:articlesLetterDescription.unknown'),
      };
    }
    return {
      price: articleItem.price,
      description: articleItem.description,
      totalPrice: totalUnsentInvoices * articleItem.price,
      VATPrice: totalUnsentInvoices * articleItem.price * 0.25,
      priceIncludingVAT: totalUnsentInvoices * articleItem.price + totalUnsentInvoices * articleItem.price * 0.25,
    };
  };
  const articleData = getArticleData();

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      disableBackdropClosing
      hideBackdrop={hideBackdrop}
      dialogTitle={t('invoice:createMemberInvoice.modalHeaderSendLetter')}
      onClose={onClose}
      onSave={onSave}
      showDialogActions
      dialogActions={
        <Box
          sx={{
            pr: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
        >
          <Button type='submit' form='invoiceForm' disabled={loading || !watch('sndDate')} disableElevation variant='contained'>
            {t('invoice:createMemberInvoice.sendInvoices')}
          </Button>
        </Box>
      }
      dialogContent={
        <Box
          sx={{
            position: 'relative',
            minHeight: '22rem',
          }}
        >
          <LoadingOverlay loading={loading} />
          <FormProvider {...formMethods}>
            <GridWrapper>
              <Grid item xs={12}>
                <Alert severity='info' sx={{ whiteSpace: 'pre-line' }}>
                  <AlertTitle>
                    {t('invoice:invoiceMissionPage.letterAlertTitle', {
                      totalUnsentInvoices,
                      totalCount: invoiceMission.data.totalCount,
                    })}
                  </AlertTitle>
                  {t('invoice:invoiceMissionPage.letterAlertText')}
                </Alert>
              </Grid>
            </GridWrapper>
            <form id='invoiceForm' onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ p: 2 }}>
                <Grid container>
                  {isSuperAdmin && (
                    <Grid item xs={12} mt={1}>
                      <ControlledCheckboxNew name='runAsTaskWithTaskService' label={t('invoice:runAsTask')} />
                    </Grid>
                  )}

                  <Grid item xs={12} mt={2}>
                    <SetSendingDate />
                  </Grid>

                  <Grid item xs={12} mt={3} mb={1}>
                    <Typography variant='h6'>{t('invoice:articlesLetter.orderPrice')}</Typography>
                  </Grid>
                  <Grid container justifyContent='space-between'>
                    <Grid item xs={6} sm={1}>
                      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {t('invoice:articlesLetter.quantity')}
                      </Typography>
                      <Typography variant='body1'>{`${totalUnsentInvoices} ${t('dashboard:piciesShort')}`}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {t('invoice:articlesLetter.article')}
                      </Typography>
                      <Typography mr={1} variant='body1'>
                        {articleData.description}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {t('invoice:articlesLetter.unitPrice')}
                      </Typography>
                      <AmountWithCurrency amount={articleData.price ?? 0} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {t('invoice:articlesLetter.total')}
                      </Typography>
                      <AmountWithCurrency amount={articleData.totalPrice ?? 0} decimals={2} />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid container mt={2} justifyContent='flex-end'>
                  <Grid item xs={12} mt={2}>
                    <Typography variant='body1' sx={{ fontWeight: 'bold', textAlign: { xs: 'left', sm: 'right' } }}>
                      {t('invoice:articlesLetter.VAT', { number: 25 })}
                    </Typography>
                    <Typography variant='body1' sx={{ fontWeight: 'bold', textAlign: { xs: 'left', sm: 'right' } }}>
                      <AmountWithCurrency amount={articleData.VATPrice ?? 0} decimals={2} />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} mt={2}>
                    <Typography variant='body1' sx={{ fontWeight: 'bold', textAlign: { xs: 'left', sm: 'right' } }}>
                      {t('invoice:createInvoice.articleSum')}
                    </Typography>
                    <Typography variant='body1' sx={{ fontWeight: 'bold', textAlign: { xs: 'left', sm: 'right' } }}>
                      <AmountWithCurrency amount={articleData.priceIncludingVAT ?? 0} decimals={2} />
                    </Typography>
                  </Grid>
                  <Typography variant='caption' sx={{ mt: 1, textAlign: { xs: 'left', sm: 'right' } }}>
                    {t('invoice:articlesLetter.paymentInfo')}
                  </Typography>
                </Grid>
              </Box>
            </form>
          </FormProvider>
        </Box>
      }
    />
  );
};
